import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { User, Mail, Phone, Calendar, CreditCard, Edit2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserDetailsSection = ({ user, onUpdate }) => {
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingMobile, setEditingMobile] = useState(false);
  const [newEmail, setNewEmail] = useState(user?.email || '');
  const [newMobile, setNewMobile] = useState(user?.mobile_number || '');
  const [loading, setLoading] = useState(false);

  const handleUpdateEmail = async () => {
    if (!newEmail || newEmail === user?.email) {
      setEditingEmail(false);
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/user/profile/email?email=${encodeURIComponent(newEmail)}`);
      toast.success('Email updated successfully');
      setEditingEmail(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
      setNewEmail(user?.email || '');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMobile = async () => {
    if (!newMobile || newMobile === user?.mobile_number) {
      setEditingMobile(false);
      return;
    }

    const mobilePattern = /^\+91[6-9][0-9]{9}$/;
    if (!mobilePattern.test(newMobile)) {
      toast.error('Invalid mobile number. Format: +91XXXXXXXXXX');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/user/profile/mobile?mobile_number=${encodeURIComponent(newMobile)}`);
      toast.success('Mobile number updated successfully');
      setEditingMobile(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update mobile number');
      setNewMobile(user?.mobile_number || '');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow-lg">
      <CardHeader className="bg-gradient-to-r from-[#0A7EA4]/5 to-[#14B8A6]/5">
        <CardTitle className="text-2xl text-[#0A7EA4] flex items-center gap-2">
          <User className="w-6 h-6" />
          Personal Information
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Read-only fields */}
          <div>
            <Label className="text-xs text-gray-500 mb-2 block">Full Name</Label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <User className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{user?.name}</span>
            </div>
          </div>

          <div>
            <Label className="text-xs text-gray-500 mb-2 block">INDoS Number</Label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <CreditCard className="w-4 h-4 text-gray-400" />
              <span className="font-mono font-medium">
                {user?.indos_number || <span className="text-gray-400 italic">Not set</span>}
              </span>
            </div>
          </div>

          <div>
            <Label className="text-xs text-gray-500 mb-2 block">Date of Birth</Label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="font-medium">
                {user?.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString() : <span className="text-gray-400 italic">Not set</span>}
              </span>
            </div>
          </div>

          <div>
            <Label className="text-xs text-gray-500 mb-2 block">Account Role</Label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <Badge className="bg-[#0A7EA4] text-white">
                {user?.role === 'sub_admin' ? 'Sub Admin' : user?.role || 'User'}
              </Badge>
            </div>
          </div>

          {/* Editable Email */}
          <div>
            <Label className="text-xs text-gray-500 mb-2 flex items-center justify-between">
              Email Address
              {!editingEmail && (
                <Button
                  onClick={() => {
                    setEditingEmail(true);
                    setNewEmail(user?.email || '');
                  }}
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-blue-600"
                >
                  <Edit2 className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              )}
            </Label>
            {editingEmail ? (
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="flex-1"
                  placeholder="your@email.com"
                />
                <Button
                  onClick={handleUpdateEmail}
                  size="sm"
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => {
                    setEditingEmail(false);
                    setNewEmail(user?.email || '');
                  }}
                  size="sm"
                  variant="outline"
                  disabled={loading}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{user?.email}</span>
              </div>
            )}
          </div>

          {/* Editable Mobile */}
          <div>
            <Label className="text-xs text-gray-500 mb-2 flex items-center justify-between">
              Mobile Number
              {!editingMobile && user?.mobile_number && (
                <Button
                  onClick={() => {
                    setEditingMobile(true);
                    setNewMobile(user?.mobile_number || '');
                  }}
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-blue-600"
                >
                  <Edit2 className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              )}
            </Label>
            {editingMobile ? (
              <div className="flex items-center gap-2">
                <Input
                  type="tel"
                  value={newMobile}
                  onChange={(e) => setNewMobile(e.target.value)}
                  className="flex-1"
                  placeholder="+919876543210"
                  maxLength={13}
                />
                <Button
                  onClick={handleUpdateMobile}
                  size="sm"
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => {
                    setEditingMobile(false);
                    setNewMobile(user?.mobile_number || '');
                  }}
                  size="sm"
                  variant="outline"
                  disabled={loading}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Phone className="w-4 h-4 text-gray-400" />
                <span className="font-mono font-medium">
                  {user?.mobile_number || <span className="text-gray-400 italic">Not set</span>}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            <strong>Note:</strong> Name, INDoS Number, and Date of Birth cannot be changed. 
            If you need to update these, please contact support.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserDetailsSection;
